import type { ContestSettings } from '@betterpool/api-types/contests-service';
import { Sport } from '@betterpool/api-types/contests-service';
import { GameStatus } from '@betterpool/api-types/games-service';

import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import type { PickForEntry } from '~/domains/contest/domains/services/interfaces/picks-for-entry.interface';
import type { PlayerPick } from '~/domains/contest/domains/gamecenter/domains/player/types/playerPick';
import { getAbbreviatedTierDisplayLabel } from '~/domains/contest/domains/common/utils/playerCardDisplay';

import type { PlayerInfo } from '../../types/player';

import parseFootballPlayerInfo from './parseFootball';
import parseGolfPlayerInfo from './parseGolf';
import parseBasePlayerInfo from './parseBase';
import parseBasketballPlayerInfo from './parseBasketball';
import parseBaseballPlayerInfo from './parseBaseball';
import parseHockeyPlayerInfo from './parseHockey';

const parsers: Record<
  Sport.FOOTBALL | Sport.GOLF | Sport.BASKETBALL | Sport.BASEBALL | Sport.HOCKEY,
  (
    pick: PlayerPoolMember,
    pickWithLiveData?: PickForEntry,
    scoreType?: ContestSettings['scoreType']
  ) => Partial<PlayerInfo>
> = {
  // TODO: make this more generic, we need a fallback parser to show basic info, eg there should always be a game involved here, at least show the game.
  [Sport.FOOTBALL]: parseFootballPlayerInfo,
  [Sport.GOLF]: parseGolfPlayerInfo,
  [Sport.BASKETBALL]: parseBasketballPlayerInfo,
  [Sport.BASEBALL]: parseBaseballPlayerInfo,
  [Sport.HOCKEY]: parseHockeyPlayerInfo,
};

const parseSpecificPlayerInfo = (
  pick: PlayerPoolMember,
  sport: Sport,
  pickWithLiveData?: PickForEntry,
  scoreType?: ContestSettings['scoreType']
) => {
  const parser = parsers[sport] ?? (() => ({}));
  return parser(pick, pickWithLiveData, scoreType);
};

const parseEntryPick = ({
  pick,
  leagueAlias,
  sport,
  pickWithLiveData,
  scoreType,
  pickIndex = 0,
  pickReuseLimit,
  canMakePicks,
}: {
  pick: PlayerPoolMember;
  leagueAlias?: string;
  sport: Sport;
  pickWithLiveData?: PickForEntry;
  scoreType?: ContestSettings['scoreType'];
  pickIndex?: number;
  pickReuseLimit?: number;
  canMakePicks?: boolean;
}): PlayerPick => ({
  // playerPoolMemberId is used for saved picks
  id: pick.playerPoolMemberId || pick.id,
  pickId: pick.playerPoolMemberId || pick.id,
  tier: {
    id: pick.tierId,
    label: getAbbreviatedTierDisplayLabel(pick.tierId),
    pickIndex,
  },
  playerPrimaryColor: pick.playerPrimaryColor,
  isRemoveable: pickWithLiveData ? pickWithLiveData.isPickChangeable : true,
  isSelectable: pickWithLiveData ? pickWithLiveData.isPlayerSelectable : pick.isPlayerSelectable,
  isAutoPick: pickWithLiveData?.isAutoPick,
  playerInfo: {
    isUpcoming: pick?.attributes?.gameStatus === GameStatus.SCHEDULED || !pickWithLiveData,
    isLocked: pickWithLiveData ? !pickWithLiveData.isPlayerSelectable : !pick.isPlayerSelectable,
    dropWorstNth: pickWithLiveData?.dropWorstNth,
    ...(canMakePicks &&
      typeof pickReuseLimit === 'number' &&
      pickReuseLimit > 1 && {
        pickedCount: {
          value: pick.previouslySelectedCount ?? 0,
          max: pickReuseLimit + 1,
        },
      }),
    ...parseBasePlayerInfo(pick, leagueAlias),
    ...parseSpecificPlayerInfo(pick, sport, pickWithLiveData, scoreType),
  },
});

export default parseEntryPick;
